import { LineStyle, type UTCTimestamp } from 'lightweight-charts';
import type { BasicStudyOverlaySettings, Candle } from '../types';
import type { BasicStudyDrawingSegmentInput } from './basicStudyDrawingPrimitive';

export type ChartPatternCategory = 'reversal' | 'continuation' | 'triangle';

export type ChartPatternKind =
  | 'doubleTop'
  | 'doubleBottom'
  | 'headAndShoulders'
  | 'inverseHeadAndShoulders'
  | 'cupAndHandle'
  | 'ascendingTriangle'
  | 'descendingTriangle'
  | 'symmetricalTriangle'
  | 'risingWedge'
  | 'fallingWedge'
  | 'bullFlag'
  | 'bearFlag'
  | 'bullPennant'
  | 'bearPennant';

interface PatternCandle {
  index: number;
  time: UTCTimestamp;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

interface SwingPivot {
  index: number;
  time: UTCTimestamp;
  price: number;
  kind: 'high' | 'low';
}

export interface ChartPatternDetection {
  kind: ChartPatternKind;
  label: string;
  category: ChartPatternCategory;
  startIndex: number;
  endIndex: number;
  score: number;
  segments: BasicStudyDrawingSegmentInput[];
}

interface PatternStyle {
  supportColor: string;
  resistanceColor: string;
  neutralColor: string;
  width: 1 | 2 | 3 | 4;
  lineStyle: LineStyle;
  showLabels: boolean;
}

interface BoundaryWindow {
  pivots: SwingPivot[];
  highs: SwingPivot[];
  lows: SwingPivot[];
  startIndex: number;
  endIndex: number;
  upperSlope: number;
  lowerSlope: number;
  upperStart: number;
  upperEnd: number;
  lowerStart: number;
  lowerEnd: number;
  startRange: number;
  endRange: number;
  span: number;
  averagePrice: number;
}

const MIN_SWING_STRENGTH = 1;
const MAX_SWING_STRENGTH = 20;
const MIN_LOOKBACK_BARS = 20;
const MAX_LOOKBACK_BARS = 500;
const MIN_PATTERN_SPAN = 4;
const MAX_PATTERN_COUNT = 20;

function clampInteger(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) {
    return min;
  }
  return Math.max(min, Math.min(max, Math.round(value)));
}

function toUnixTimestamp(time: string): UTCTimestamp | null {
  const timestampMs = Date.parse(time);
  if (!Number.isFinite(timestampMs)) {
    return null;
  }

  const timestampSeconds = Math.floor(timestampMs / 1000);
  return Number.isFinite(timestampSeconds) ? (timestampSeconds as UTCTimestamp) : null;
}

function toLineStyle(style: BasicStudyOverlaySettings['style']['lineStyle']): LineStyle {
  if (style === 'dashed') {
    return LineStyle.Dashed;
  }
  if (style === 'dotted') {
    return LineStyle.Dotted;
  }
  return LineStyle.Solid;
}

function normalizeCandles(candles: Candle[], lookbackBars: number): PatternCandle[] {
  const startIndex = Math.max(0, candles.length - lookbackBars);
  const output: PatternCandle[] = [];

  for (let index = startIndex; index < candles.length; index += 1) {
    const candle = candles[index];
    const time = toUnixTimestamp(candle.time);
    if (
      time === null ||
      !Number.isFinite(candle.open) ||
      !Number.isFinite(candle.high) ||
      !Number.isFinite(candle.low) ||
      !Number.isFinite(candle.close)
    ) {
      continue;
    }

    output.push({
      index,
      time,
      open: candle.open,
      high: candle.high,
      low: candle.low,
      close: candle.close,
      volume: Number.isFinite(candle.volume) ? candle.volume : 0,
    });
  }

  return output;
}

function buildStyle(settings: BasicStudyOverlaySettings): PatternStyle {
  return {
    supportColor: settings.style.primaryColor,
    resistanceColor: settings.style.secondaryColor,
    neutralColor: settings.style.tertiaryColor,
    width: settings.style.lineWidth,
    lineStyle: toLineStyle(settings.style.lineStyle),
    showLabels: settings.visibility.showSeriesLabels,
  };
}

function relativeDistance(left: number, right: number): number {
  const denominator = Math.max(Math.abs(left), Math.abs(right), 1e-9);
  return Math.abs(left - right) / denominator;
}

function areNear(left: number, right: number, tolerance: number): boolean {
  return relativeDistance(left, right) <= tolerance;
}

function segment(
  points: Array<Pick<SwingPivot, 'time' | 'price'>>,
  color: string,
  style: PatternStyle,
  label?: string,
): BasicStudyDrawingSegmentInput {
  return {
    points: points.map((point) => ({
      time: point.time,
      price: point.price,
    })),
    color,
    width: style.width,
    style: style.lineStyle,
    label: style.showLabels ? label : undefined,
    labelColor: style.showLabels ? color : undefined,
    labelPosition: 'right',
  };
}

function horizontalSegment(
  left: SwingPivot,
  right: SwingPivot,
  price: number,
  color: string,
  style: PatternStyle,
  label?: string,
): BasicStudyDrawingSegmentInput {
  return segment(
    [
      { time: left.time, price },
      { time: right.time, price },
    ],
    color,
    style,
    label,
  );
}

function detectSwingPivots(candles: PatternCandle[], strength: number): SwingPivot[] {
  const raw: SwingPivot[] = [];
  if (candles.length < strength * 2 + 1) {
    return raw;
  }

  for (let index = strength; index < candles.length - strength; index += 1) {
    const candle = candles[index];
    let isHigh = true;
    let isLow = true;

    for (let offset = 1; offset <= strength; offset += 1) {
      const left = candles[index - offset];
      const right = candles[index + offset];
      if (candle.high < left.high || candle.high < right.high) {
        isHigh = false;
      }
      if (candle.low > left.low || candle.low > right.low) {
        isLow = false;
      }
    }

    if (isHigh && !isLow) {
      raw.push({ index: candle.index, time: candle.time, price: candle.high, kind: 'high' });
    } else if (isLow && !isHigh) {
      raw.push({ index: candle.index, time: candle.time, price: candle.low, kind: 'low' });
    }
  }

  return raw.reduce<SwingPivot[]>((pivots, pivot) => {
    const previous = pivots[pivots.length - 1];
    if (!previous || previous.kind !== pivot.kind) {
      pivots.push(pivot);
      return pivots;
    }

    const replaceHigh = pivot.kind === 'high' && pivot.price > previous.price;
    const replaceLow = pivot.kind === 'low' && pivot.price < previous.price;
    if (replaceHigh || replaceLow) {
      pivots[pivots.length - 1] = pivot;
    }
    return pivots;
  }, []);
}

function pushDoublePatterns(
  detections: ChartPatternDetection[],
  pivots: SwingPivot[],
  style: PatternStyle,
  tolerance: number,
): void {
  for (let index = 0; index <= pivots.length - 3; index += 1) {
    const first = pivots[index];
    const middle = pivots[index + 1];
    const last = pivots[index + 2];

    if (first.kind === 'high' && middle.kind === 'low' && last.kind === 'high') {
      const resistance = (first.price + last.price) / 2;
      const depth = resistance - middle.price;
      if (areNear(first.price, last.price, tolerance) && depth / resistance >= tolerance * 1.5) {
        detections.push({
          kind: 'doubleTop',
          label: 'Double Top',
          category: 'reversal',
          startIndex: first.index,
          endIndex: last.index,
          score: 100 + last.index + depth / resistance,
          segments: [
            horizontalSegment(first, last, resistance, style.resistanceColor, style, 'Double Top'),
            horizontalSegment(middle, last, middle.price, style.neutralColor, style),
          ],
        });
      }
    }

    if (first.kind === 'low' && middle.kind === 'high' && last.kind === 'low') {
      const support = (first.price + last.price) / 2;
      const depth = middle.price - support;
      if (areNear(first.price, last.price, tolerance) && depth / support >= tolerance * 1.5) {
        detections.push({
          kind: 'doubleBottom',
          label: 'Double Bottom',
          category: 'reversal',
          startIndex: first.index,
          endIndex: last.index,
          score: 100 + last.index + depth / support,
          segments: [
            horizontalSegment(first, last, support, style.supportColor, style, 'Double Bottom'),
            horizontalSegment(middle, last, middle.price, style.neutralColor, style),
          ],
        });
      }
    }
  }
}

function pushHeadAndShouldersPatterns(
  detections: ChartPatternDetection[],
  pivots: SwingPivot[],
  style: PatternStyle,
  tolerance: number,
): void {
  for (let index = 0; index <= pivots.length - 5; index += 1) {
    const first = pivots[index];
    const second = pivots[index + 1];
    const third = pivots[index + 2];
    const fourth = pivots[index + 3];
    const fifth = pivots[index + 4];

    if (
      first.kind === 'high' &&
      second.kind === 'low' &&
      third.kind === 'high' &&
      fourth.kind === 'low' &&
      fifth.kind === 'high'
    ) {
      const shoulderAverage = (first.price + fifth.price) / 2;
      const necklineAverage = (second.price + fourth.price) / 2;
      const headHeight = third.price - shoulderAverage;
      if (
        third.price > first.price &&
        third.price > fifth.price &&
        areNear(first.price, fifth.price, tolerance * 1.5) &&
        areNear(second.price, fourth.price, tolerance * 2) &&
        headHeight / shoulderAverage >= tolerance
      ) {
        detections.push({
          kind: 'headAndShoulders',
          label: 'Head and Shoulders',
          category: 'reversal',
          startIndex: first.index,
          endIndex: fifth.index,
          score: 120 + fifth.index + headHeight / shoulderAverage,
          segments: [
            segment([first, third, fifth], style.resistanceColor, style, 'Head and Shoulders'),
            segment([second, fourth], style.neutralColor, style),
            horizontalSegment(second, fourth, necklineAverage, style.neutralColor, style),
          ],
        });
      }
    }

    if (
      first.kind === 'low' &&
      second.kind === 'high' &&
      third.kind === 'low' &&
      fourth.kind === 'high' &&
      fifth.kind === 'low'
    ) {
      const shoulderAverage = (first.price + fifth.price) / 2;
      const necklineAverage = (second.price + fourth.price) / 2;
      const headDepth = shoulderAverage - third.price;
      if (
        third.price < first.price &&
        third.price < fifth.price &&
        areNear(first.price, fifth.price, tolerance * 1.5) &&
        areNear(second.price, fourth.price, tolerance * 2) &&
        headDepth / shoulderAverage >= tolerance
      ) {
        detections.push({
          kind: 'inverseHeadAndShoulders',
          label: 'Inverse Head and Shoulders',
          category: 'reversal',
          startIndex: first.index,
          endIndex: fifth.index,
          score: 120 + fifth.index + headDepth / shoulderAverage,
          segments: [
            segment([first, third, fifth], style.supportColor, style, 'Inverse Head and Shoulders'),
            segment([second, fourth], style.neutralColor, style),
            horizontalSegment(second, fourth, necklineAverage, style.neutralColor, style),
          ],
        });
      }
    }
  }
}

function pushCupAndHandlePatterns(
  detections: ChartPatternDetection[],
  pivots: SwingPivot[],
  style: PatternStyle,
  tolerance: number,
): void {
  for (let index = 0; index <= pivots.length - 5; index += 1) {
    const leftRim = pivots[index];
    const cupLow = pivots[index + 1];
    const rightRim = pivots[index + 2];
    const handleLow = pivots[index + 3];
    const breakout = pivots[index + 4];

    if (
      leftRim.kind !== 'high' ||
      cupLow.kind !== 'low' ||
      rightRim.kind !== 'high' ||
      handleLow.kind !== 'low' ||
      breakout.kind !== 'high'
    ) {
      continue;
    }

    const rim = (leftRim.price + rightRim.price) / 2;
    const cupDepth = rim - cupLow.price;
    const handleDepth = rightRim.price - handleLow.price;
    const handleAboveCup = handleLow.price > cupLow.price + cupDepth * 0.45;
    if (
      areNear(leftRim.price, rightRim.price, tolerance * 1.5) &&
      cupDepth / rim >= tolerance * 3 &&
      handleDepth > 0 &&
      handleDepth < cupDepth * 0.65 &&
      handleAboveCup &&
      breakout.price >= rightRim.price * (1 - tolerance)
    ) {
      detections.push({
        kind: 'cupAndHandle',
        label: 'Cup and Handle',
        category: 'reversal',
        startIndex: leftRim.index,
        endIndex: breakout.index,
        score: 110 + breakout.index + cupDepth / rim,
        segments: [
          horizontalSegment(leftRim, rightRim, rim, style.resistanceColor, style, 'Cup and Handle'),
          segment([leftRim, cupLow, rightRim], style.supportColor, style),
          segment([rightRim, handleLow, breakout], style.neutralColor, style),
        ],
      });
    }
  }
}

function lineValue(first: SwingPivot, last: SwingPivot, index: number): number {
  const span = last.index - first.index;
  if (span === 0) {
    return last.price;
  }
  return first.price + ((last.price - first.price) / span) * (index - first.index);
}

function buildBoundaryWindow(pivots: SwingPivot[]): BoundaryWindow | null {
  const highs = pivots.filter((pivot) => pivot.kind === 'high');
  const lows = pivots.filter((pivot) => pivot.kind === 'low');
  if (highs.length < 2 || lows.length < 2) {
    return null;
  }

  const firstHigh = highs[0];
  const lastHigh = highs[highs.length - 1];
  const firstLow = lows[0];
  const lastLow = lows[lows.length - 1];
  const startIndex = Math.min(firstHigh.index, firstLow.index);
  const endIndex = Math.max(lastHigh.index, lastLow.index);
  const span = endIndex - startIndex;
  if (span < MIN_PATTERN_SPAN || firstHigh.index === lastHigh.index || firstLow.index === lastLow.index) {
    return null;
  }

  const upperStart = lineValue(firstHigh, lastHigh, startIndex);
  const upperEnd = lineValue(firstHigh, lastHigh, endIndex);
  const lowerStart = lineValue(firstLow, lastLow, startIndex);
  const lowerEnd = lineValue(firstLow, lastLow, endIndex);
  const startRange = upperStart - lowerStart;
  const endRange = upperEnd - lowerEnd;
  if (startRange <= 0 || endRange <= 0) {
    return null;
  }

  const upperSlope = (lastHigh.price - firstHigh.price) / Math.max(1, lastHigh.index - firstHigh.index);
  const lowerSlope = (lastLow.price - firstLow.price) / Math.max(1, lastLow.index - firstLow.index);
  const averagePrice = (upperStart + upperEnd + lowerStart + lowerEnd) / 4;

  return {
    pivots,
    highs,
    lows,
    startIndex,
    endIndex,
    upperSlope,
    lowerSlope,
    upperStart,
    upperEnd,
    lowerStart,
    lowerEnd,
    startRange,
    endRange,
    span,
    averagePrice,
  };
}

function boundarySegments(
  window: BoundaryWindow,
  style: PatternStyle,
  label: string,
): BasicStudyDrawingSegmentInput[] {
  return [
    segment(
      [window.highs[0], window.highs[window.highs.length - 1]],
      style.resistanceColor,
      style,
      label,
    ),
    segment([window.lows[0], window.lows[window.lows.length - 1]], style.supportColor, style),
  ];
}

function precedingMove(candles: PatternCandle[], startIndex: number, span: number): number | null {
  const startOffset = candles.findIndex((candle) => candle.index >= startIndex);
  if (startOffset <= 0) {
    return null;
  }

  const poleStartOffset = Math.max(0, startOffset - Math.max(3, Math.round(span)));
  const poleStart = candles[poleStartOffset];
  const poleEnd = candles[startOffset];
  if (!poleStart || !poleEnd || poleStart.close === 0) {
    return null;
  }

  return (poleEnd.close - poleStart.close) / Math.abs(poleStart.close);
}

function pushBoundaryPattern(
  detections: ChartPatternDetection[],
  kind: ChartPatternKind,
  label: string,
  category: ChartPatternCategory,
  window: BoundaryWindow,
  style: PatternStyle,
  scoreBase: number,
): void {
  detections.push({
    kind,
    label,
    category,
    startIndex: window.startIndex,
    endIndex: window.endIndex,
    score: scoreBase + window.endIndex + (window.startRange - window.endRange) / window.averagePrice,
    segments: boundarySegments(window, style, label),
  });
}

function pushBoundaryPatterns(
  detections: ChartPatternDetection[],
  candles: PatternCandle[],
  pivots: SwingPivot[],
  style: PatternStyle,
  tolerance: number,
): void {
  for (let end = 3; end < pivots.length; end += 1) {
    const earliestStart = Math.max(0, end - 7);
    for (let start = earliestStart; start <= end - 3; start += 1) {
      const window = buildBoundaryWindow(pivots.slice(start, end + 1));
      if (!window) {
        continue;
      }

      const slopeThreshold = (window.averagePrice * tolerance) / Math.max(1, window.span);
      const flatUpper = Math.abs(window.upperEnd - window.upperStart) / window.averagePrice <= tolerance * 1.2;
      const flatLower = Math.abs(window.lowerEnd - window.lowerStart) / window.averagePrice <= tolerance * 1.2;
      const upperDown = window.upperSlope < -slopeThreshold;
      const upperUp = window.upperSlope > slopeThreshold;
      const lowerDown = window.lowerSlope < -slopeThreshold;
      const lowerUp = window.lowerSlope > slopeThreshold;
      const converging = window.endRange < window.startRange * 0.82;
      const parallel =
        Math.abs(window.upperSlope - window.lowerSlope) <= slopeThreshold * 1.5 &&
        window.endRange <= window.startRange * 1.15;
      const move = precedingMove(candles, window.startIndex, window.span);
      const hasBullPole = move !== null && move >= tolerance * 4;
      const hasBearPole = move !== null && move <= -tolerance * 4;

      if (flatUpper && lowerUp && converging) {
        pushBoundaryPattern(detections, 'ascendingTriangle', 'Ascending Triangle', 'triangle', window, style, 80);
        continue;
      }

      if (flatLower && upperDown && converging) {
        pushBoundaryPattern(detections, 'descendingTriangle', 'Descending Triangle', 'triangle', window, style, 80);
        continue;
      }

      if (upperDown && lowerUp && converging) {
        if (hasBullPole) {
          pushBoundaryPattern(detections, 'bullPennant', 'Bull Pennant', 'continuation', window, style, 95);
        } else if (hasBearPole) {
          pushBoundaryPattern(detections, 'bearPennant', 'Bear Pennant', 'continuation', window, style, 95);
        } else {
          pushBoundaryPattern(detections, 'symmetricalTriangle', 'Symmetrical Triangle', 'triangle', window, style, 75);
        }
        continue;
      }

      if (upperUp && lowerUp && converging) {
        pushBoundaryPattern(detections, 'risingWedge', 'Rising Wedge', 'continuation', window, style, 70);
        continue;
      }

      if (upperDown && lowerDown && converging) {
        pushBoundaryPattern(detections, 'fallingWedge', 'Falling Wedge', 'continuation', window, style, 70);
        continue;
      }

      if (parallel && hasBullPole && window.upperSlope <= slopeThreshold) {
        pushBoundaryPattern(detections, 'bullFlag', 'Bull Flag', 'continuation', window, style, 65);
      } else if (parallel && hasBearPole && window.lowerSlope >= -slopeThreshold) {
        pushBoundaryPattern(detections, 'bearFlag', 'Bear Flag', 'continuation', window, style, 65);
      }
    }
  }
}

function isCategoryVisible(category: ChartPatternCategory, settings: BasicStudyOverlaySettings): boolean {
  if (category === 'reversal') {
    return settings.visibility.showPrimary;
  }
  if (category === 'continuation') {
    return settings.visibility.showSecondary;
  }
  return settings.visibility.showTertiary;
}

function overlapRatio(left: ChartPatternDetection, right: ChartPatternDetection): number {
  const start = Math.max(left.startIndex, right.startIndex);
  const end = Math.min(left.endIndex, right.endIndex);
  if (end <= start) {
    return 0;
  }
  const leftSpan = Math.max(1, left.endIndex - left.startIndex);
  const rightSpan = Math.max(1, right.endIndex - right.startIndex);
  return (end - start) / Math.min(leftSpan, rightSpan);
}

function selectDetections(
  detections: ChartPatternDetection[],
  settings: BasicStudyOverlaySettings,
): ChartPatternDetection[] {
  const maxPatterns = clampInteger(settings.inputs.length3, 1, MAX_PATTERN_COUNT);
  const selected: ChartPatternDetection[] = [];
  const sorted = [...detections]
    .filter((detection) => isCategoryVisible(detection.category, settings))
    .sort((left, right) => right.endIndex - left.endIndex || right.score - left.score);

  for (const detection of sorted) {
    const conflicts = selected.some(
      (existing) =>
        existing.kind === detection.kind ||
        (existing.category === detection.category && overlapRatio(existing, detection) > 0.65),
    );
    if (conflicts) {
      continue;
    }
    selected.push(detection);
    if (selected.length >= maxPatterns) {
      break;
    }
  }

  return selected.sort((left, right) => left.startIndex - right.startIndex || left.endIndex - right.endIndex);
}

export function computeChartPatternDetections(
  candles: Candle[],
  settings: BasicStudyOverlaySettings,
): ChartPatternDetection[] {
  const swingStrength = clampInteger(settings.inputs.length, MIN_SWING_STRENGTH, MAX_SWING_STRENGTH);
  const lookbackBars = clampInteger(settings.inputs.length2, MIN_LOOKBACK_BARS, MAX_LOOKBACK_BARS);
  const tolerance = Math.max(0.001, Math.min(0.1, settings.inputs.multiplier / 100));
  const normalizedCandles = normalizeCandles(candles, lookbackBars);
  if (normalizedCandles.length < swingStrength * 2 + 3) {
    return [];
  }

  const pivots = detectSwingPivots(normalizedCandles, swingStrength);
  if (pivots.length < 3) {
    return [];
  }

  const style = buildStyle(settings);
  const detections: ChartPatternDetection[] = [];

  pushDoublePatterns(detections, pivots, style, tolerance);
  pushHeadAndShouldersPatterns(detections, pivots, style, tolerance);
  pushCupAndHandlePatterns(detections, pivots, style, tolerance);
  pushBoundaryPatterns(detections, normalizedCandles, pivots, style, tolerance);

  return selectDetections(detections, settings);
}

export function buildChartPatternDrawingSegments(
  candles: Candle[],
  settings: BasicStudyOverlaySettings,
): BasicStudyDrawingSegmentInput[] {
  return computeChartPatternDetections(candles, settings).flatMap((detection) => detection.segments);
}
